import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STEPS = ["Contact", "Property", "Scope", "Message"];

export default function Consultation() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [f, setF] = useState({
    name: "", email: "", phone: "",
    project_location: "", property_type: "Apartment",
    project_type: "residential", work_type: "new",
    property_size: "", services: [], budget_range: "",
    start_date: "", description: "", preferred_contact: "email", consent: true,
  });

  useEffect(() => { api.get("/services").then((r) => setServices(r.data)); }, []);

  const set = (k, v) => setF({ ...f, [k]: v });
  const toggleService = (title) => setF({ ...f, services: f.services.includes(title) ? f.services.filter((s) => s !== title) : [...f.services, title] });

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!f.consent) { toast.error("Please accept privacy consent."); return; }
    if (!f.name || !f.email) { toast.error("Name and email are required."); setStep(0); return; }
    setLoading(true);
    try {
      // capture UTM from search params
      const url = new URLSearchParams(window.location.search);
      const utm = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => { if (url.get(k)) utm[k] = url.get(k); });
      await api.post("/enquiries", { ...f, utm });
      nav("/thank-you");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="page-consultation" className="bg-[#F9F8F6] min-h-screen">
      <section className="container-editorial pt-16 md:pt-24 pb-8 max-w-4xl">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#7A736C] mb-6">Book a Consultation</div>
        <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.02]">Tell us a little about your space and your life.</h1>
        <p className="mt-6 text-[#4A4642] max-w-2xl leading-relaxed">A short form, four short steps. Once submitted, our team will be in touch within 1–2 business days to schedule your consultation.</p>
      </section>

      <section className="container-editorial pb-16 max-w-4xl">
        <div className="flex items-center gap-4 mb-10">
          {STEPS.map((s, i) => (
            <div key={s} data-testid={`step-indicator-${i}`} className={`flex items-center gap-3 text-xs uppercase tracking-[0.2em] ${i === step ? "text-[#A85C42]" : "text-[#7A736C]"}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center border ${i === step ? "border-[#A85C42] bg-[#A85C42] text-white" : "border-[#D5D0C8]"}`}>{i + 1}</span>
              <span className="hidden md:inline">{s}</span>
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#D5D0C8] rounded-sm p-6 md:p-10">
          {step === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full name" required><input required data-testid="cons-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="input" /></Field>
              <Field label="Email" required><input required type="email" data-testid="cons-email" value={f.email} onChange={(e) => set("email", e.target.value)} className="input" /></Field>
              <Field label="Phone / WhatsApp"><input data-testid="cons-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className="input" /></Field>
              <Field label="Preferred contact">
                <select data-testid="cons-preferred" value={f.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)} className="input">
                  <option value="email">Email</option><option value="phone">Phone</option><option value="whatsapp">WhatsApp</option>
                </select>
              </Field>
            </div>
          )}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Project location"><input data-testid="cons-location" value={f.project_location} onChange={(e) => set("project_location", e.target.value)} placeholder="City, area" className="input" /></Field>
              <Field label="Property type">
                <select data-testid="cons-property-type" value={f.property_type} onChange={(e) => set("property_type", e.target.value)} className="input">
                  <option>Apartment</option><option>Villa / Independent home</option><option>Studio</option><option>Office</option><option>Retail</option><option>Restaurant / Hospitality</option><option>Other</option>
                </select>
              </Field>
              <Field label="Residential or Commercial">
                <select data-testid="cons-project-type" value={f.project_type} onChange={(e) => set("project_type", e.target.value)} className="input">
                  <option value="residential">Residential</option><option value="commercial">Commercial</option>
                </select>
              </Field>
              <Field label="New construction or Renovation">
                <select data-testid="cons-work-type" value={f.work_type} onChange={(e) => set("work_type", e.target.value)} className="input">
                  <option value="new">New construction</option><option value="renovation">Renovation</option>
                </select>
              </Field>
              <Field label="Property size (sq ft)"><input data-testid="cons-size" value={f.property_size} onChange={(e) => set("property_size", e.target.value)} className="input" /></Field>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <span className="block text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-3">Services you're interested in</span>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {services.map((s) => (
                    <label key={s.id} data-testid={`cons-service-${s.slug}`} className={`flex items-center gap-3 border p-3 rounded-sm cursor-pointer transition ${f.services.includes(s.title) ? "border-[#A85C42] bg-[#F2EFE9]" : "border-[#D5D0C8]"}`}>
                      <input type="checkbox" checked={f.services.includes(s.title)} onChange={() => toggleService(s.title)} />
                      <span className="text-sm">{s.title}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Budget range">
                  <select data-testid="cons-budget" value={f.budget_range} onChange={(e) => set("budget_range", e.target.value)} className="input">
                    <option value="">Prefer to discuss</option>
                    <option>Under ₹10 L</option><option>₹10–25 L</option><option>₹25–50 L</option><option>₹50 L – 1 Cr</option><option>₹1 – 2 Cr</option><option>₹2 Cr +</option>
                  </select>
                </Field>
                <Field label="Expected start date"><input type="date" data-testid="cons-start-date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} className="input" /></Field>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-5">
              <Field label="Tell us more about your project"><textarea rows={6} data-testid="cons-description" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="What are you hoping for? Any references, images or feelings you can describe help enormously." className="input" /></Field>
              <label className="flex items-start gap-3 text-sm text-[#4A4642]">
                <input type="checkbox" data-testid="cons-consent" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1" />
                <span>I agree to Hiraya storing my details to respond to my enquiry.</span>
              </label>
            </div>
          )}
          <div className="flex justify-between mt-10 pt-6 border-t border-[#D5D0C8]">
            <button type="button" onClick={back} disabled={step === 0} data-testid="cons-back" className="text-xs uppercase tracking-[0.24em] px-6 py-3 border border-[#D5D0C8] rounded-sm disabled:opacity-40 hover:border-[#2B2927]">Back</button>
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={next} data-testid="cons-next" className="px-8 py-3 bg-[#2B2927] text-[#F9F8F6] text-xs uppercase tracking-[0.24em] rounded-sm hover:bg-[#A85C42] transition-colors">Continue</button>
            ) : (
              <button type="button" onClick={submit} disabled={loading} data-testid="cons-submit" className="px-8 py-3 bg-[#A85C42] text-[#F9F8F6] text-xs uppercase tracking-[0.24em] rounded-sm hover:bg-[#2B2927] transition-colors disabled:opacity-60">{loading ? "Submitting…" : "Submit enquiry"}</button>
            )}
          </div>
        </div>
      </section>
      <style>{`.input{background:white;border:1px solid #D5D0C8;padding:14px 16px;width:100%;font-size:14px;border-radius:3px;outline:none}.input:focus{border-color:#A85C42}`}</style>
    </div>
  );
}
function Field({ label, required, children }) {
  return <label className="block"><span className="block text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-2">{label}{required && <span className="text-[#A85C42]"> *</span>}</span>{children}</label>;
}
